// @ts-ignore
import { METRIC_TYPE } from "../../constants";

type TherapyItem = {
  INDEX: number;
  TITLE: string;
  TEXT: string;
};

type AlluraTherapyRow = {
  INDEX: number;
  TITLE: string;
  IMPROVE_AREAS: TherapyItem[];
};

// Keyed by METRIC_TYPE — 7 metrics × 3 rows × 3 cards = 63 cards total
export const ALLURA_THERAPY_DATA: Record<number, AlluraTherapyRow[]> = {
  // Page 7 — Sympathetic / Parasympathetic Activation
  [METRIC_TYPE.SDNN]: [
    {
      INDEX: 1,
      TITLE: "Peptide Therapies",
      IMPROVE_AREAS: [
        {
          INDEX: 1,
          TITLE: "Selank",
          TEXT: "Enhances GABAergic signaling and improves vagal tone, reducing sympathetic overactivation and supporting parasympathetic recovery.",
        },
        {
          INDEX: 2,
          TITLE: "Semax",
          TEXT: "Modulates the HPA axis and autonomic nervous system balance, helping normalize the sympathetic-to-parasympathetic ratio under stress.",
        },
        {
          INDEX: 3,
          TITLE: "Thymosin Alpha-1",
          TEXT: "Reduces immune-driven sympathetic activation; by dampening chronic inflammatory signaling it restores autonomic equilibrium and parasympathetic tone.",
        },
      ],
    },
    {
      INDEX: 2,
      TITLE: "Additional Therapies",
      IMPROVE_AREAS: [
        {
          INDEX: 1,
          TITLE: "NAD+ (low-dose IV)",
          TEXT: "Low-dose NAD+ IV therapy supports CNS mitochondrial energy production, improving neuronal stress resilience without overstimulation and promoting a calmer nervous system baseline.",
        },
        {
          INDEX: 2,
          TITLE: "Epitalon",
          TEXT: "Helps regulate circadian rhythms, which are closely tied to autonomic nervous system cycling; better circadian alignment improves sleep-driven parasympathetic activation.",
        },
        {
          INDEX: 3,
          TITLE: "DSIP",
          TEXT: "Delta sleep-inducing peptide helps normalize sleep architecture, which is essential for nocturnal parasympathetic dominance and overnight nervous system recovery.",
        },
      ],
    },
    {
      INDEX: 3,
      TITLE: "Combination Protocols",
      IMPROVE_AREAS: [
        {
          INDEX: 1,
          TITLE: "Selank + Breathwork",
          TEXT: "Combines CNS calming from Selank with pranayama to amplify vagal tone; diaphragmatic breathing directly activates the parasympathetic pathway.",
        },
        {
          INDEX: 2,
          TITLE: "NAD+ + Sleep Protocol",
          TEXT: "Low-dose NAD+ supports mitochondrial repair during deep sleep, enhancing overnight parasympathetic recovery and reducing accumulated stress.",
        },
        {
          INDEX: 3,
          TITLE: "Thymosin Alpha-1 + Sauna",
          TEXT: "Reduces inflammatory load while promoting heat-induced relaxation, synergistically boosting parasympathetic activation.",
        },
      ],
    },
  ],

  // Page 8 — Ventilation Efficiency
  [METRIC_TYPE.VENTILATION_EFFICIENCY]: [
    {
      INDEX: 1,
      TITLE: "Peptide Therapies",
      IMPROVE_AREAS: [
        {
          INDEX: 1,
          TITLE: "BPC-157",
          TEXT: "Enhances pulmonary microcirculation and upregulates endothelial nitric oxide signaling, improving blood flow to the lung surface for more efficient O\u2082\u2013CO\u2082 exchange.",
        },
        {
          INDEX: 2,
          TITLE: "TB-500",
          TEXT: "Accelerates respiratory muscle and lung tissue recovery; by promoting repair and reducing airway inflammation it maintains optimal ventilation capacity.",
        },
        {
          INDEX: 3,
          TITLE: "GHK-Cu",
          TEXT: "Potent anti-fibrotic properties that improve oxygen diffusion across the alveolar membrane and support connective tissue remodeling in the lungs.",
        },
      ],
    },
    {
      INDEX: 2,
      TITLE: "Additional Therapies",
      IMPROVE_AREAS: [
        {
          INDEX: 1,
          TITLE: "NAD+ (IV)",
          TEXT: "NAD+ IV therapy improves mitochondrial efficiency of respiratory muscles, enhancing their oxidative capacity and supporting sustained, efficient oxygen utilization throughout the day.",
        },
        {
          INDEX: 2,
          TITLE: "BPC-157 + Breathwork",
          TEXT: "Combining BPC-157 with tummo or resistance breathing devices amplifies pulmonary microvascular health while mechanically increasing tidal volume.",
        },
        {
          INDEX: 3,
          TITLE: "SS-31 (Elamipretide)",
          TEXT: "Targets the inner mitochondrial membrane in respiratory cells, improving energy efficiency during gas exchange and reducing oxidative damage to lung tissue.",
        },
      ],
    },
    {
      INDEX: 3,
      TITLE: "Combination Protocols",
      IMPROVE_AREAS: [
        {
          INDEX: 1,
          TITLE: "TB-500 + Zone 4 Training",
          TEXT: "Pairing TB-500 with HIIT accelerates respiratory muscle adaptation; TB-500 supports tissue recovery while intervals push ventilatory capacity upward.",
        },
        {
          INDEX: 2,
          TITLE: "GHK-Cu + Weight Mgmt",
          TEXT: "GHK-Cu combined with maintaining a healthy body weight optimizes lung compliance; reducing mechanical compression while remodeling connective tissue improves overall efficiency.",
        },
        {
          INDEX: 3,
          TITLE: "NAD+ + Endurance",
          TEXT: "NAD+ IV cycles paired with Zone 2\u20133 training enhance mitochondrial density in respiratory muscles, building a stronger aerobic base for sustained ventilation.",
        },
      ],
    },
  ],

  // Page 9 — Breathing Coordination
  [METRIC_TYPE.BREATHING_STABILITY]: [
    {
      INDEX: 1,
      TITLE: "Peptide Therapies",
      IMPROVE_AREAS: [
        {
          INDEX: 1,
          TITLE: "Selank",
          TEXT: "Stabilizes respiratory rhythm by calming the CNS and reducing erratic breathing patterns associated with stress, supporting consistent diaphragm\u2013CNS coordination.",
        },
        {
          INDEX: 2,
          TITLE: "Semax",
          TEXT: "Improves brainstem respiratory control centers, enhancing neural signaling that coordinates inhalation and exhalation timing for smoother, more regular breathing.",
        },
        {
          INDEX: 3,
          TITLE: "DSIP",
          TEXT: "Normalizes respiratory rhythm during sleep, with carry-over effects into waking breathing patterns; better sleep breathing improves daytime coordination.",
        },
      ],
    },
    {
      INDEX: 2,
      TITLE: "Additional Therapies",
      IMPROVE_AREAS: [
        {
          INDEX: 1,
          TITLE: "NAD+ (low-dose IV)",
          TEXT: "Low-dose NAD+ enhances neuronal energy availability in the brainstem, stabilizing the respiratory control centers that govern breathing rate and depth coordination.",
        },
        {
          INDEX: 2,
          TITLE: "Selank + Magnesium",
          TEXT: "Selank paired with magnesium-rich nutrition (Swiss chard, avocado) creates a synergistic calming effect on the nervous system, promoting steadier breathing patterns.",
        },
        {
          INDEX: 3,
          TITLE: "Semax + L-Theanine",
          TEXT: "Supports brainstem respiratory control and cortical relaxation simultaneously, reducing stress-driven hyperventilation.",
        },
      ],
    },
    {
      INDEX: 3,
      TITLE: "Combination Protocols",
      IMPROVE_AREAS: [
        {
          INDEX: 1,
          TITLE: "Selank + Box Breathing",
          TEXT: "Enhances CNS calming while training voluntary respiratory control, creating lasting breathing coordination improvements.",
        },
        {
          INDEX: 2,
          TITLE: "DSIP + Sleep Optimization",
          TEXT: "Normalizes nighttime breathing rhythms, resetting the respiratory pacemaker and improving daytime coordination.",
        },
        {
          INDEX: 3,
          TITLE: "NAD+ + Cold Exposure",
          TEXT: "NAD+ combined with cold exposure training teaches controlled deep breathing under stress, strengthening the neural pathways that coordinate respiratory muscles.",
        },
      ],
    },
  ],

  // Page 10 — Lung Utilization
  [METRIC_TYPE.LUNG_FITNESS]: [
    {
      INDEX: 1,
      TITLE: "Peptide Therapies",
      IMPROVE_AREAS: [
        {
          INDEX: 1,
          TITLE: "TB-500",
          TEXT: "Improves muscle elasticity and thoracic compliance, allowing the chest wall and diaphragm to expand more fully during inhalation, maximizing lung recruitment.",
        },
        {
          INDEX: 2,
          TITLE: "BPC-157",
          TEXT: "Supports diaphragm recovery and repair, ensuring the primary respiratory muscle functions at full capacity to drive adequate tidal volume with each breath.",
        },
        {
          INDEX: 3,
          TITLE: "GHK-Cu",
          TEXT: "Improves connective tissue remodeling throughout the thorax, maintaining the structural integrity needed for full lung expansion and optimal tidal volume.",
        },
      ],
    },
    {
      INDEX: 2,
      TITLE: "Additional Therapies",
      IMPROVE_AREAS: [
        {
          INDEX: 1,
          TITLE: "NAD+ (IV)",
          TEXT: "NAD+ improves muscular oxidative capacity in the respiratory muscles, particularly the diaphragm and intercostals, enabling sustained, full lung utilization at rest.",
        },
        {
          INDEX: 2,
          TITLE: "TB-500 + Anti-inflammatories",
          TEXT: "TB-500 paired with natural anti-inflammatory foods (turmeric, pumpkin) reduces airway inflammation while repairing thoracic muscle tissue for better expansion.",
        },
        {
          INDEX: 3,
          TITLE: "BPC-157 + Carotenoids",
          TEXT: "Supports both diaphragm recovery and alveolar surface health, improving the mechanics and efficiency of each breath.",
        },
      ],
    },
    {
      INDEX: 3,
      TITLE: "Combination Protocols",
      IMPROVE_AREAS: [
        {
          INDEX: 1,
          TITLE: "TB-500 + Interval Training",
          TEXT: "Pairing TB-500 with Zone 4 intervals pushes forced vital capacity (FVC) higher while the peptide accelerates recovery of the respiratory muscles.",
        },
        {
          INDEX: 2,
          TITLE: "GHK-Cu + Posture Work",
          TEXT: "Supports thoracic connective tissue health; good posture mechanically enables fuller lung expansion at rest.",
        },
        {
          INDEX: 3,
          TITLE: "NAD+ + Breathwork",
          TEXT: "NAD+ IV cycles paired with breathing resistance training enhance both the energy supply and mechanical strength of respiratory muscles for maximum utilization.",
        },
      ],
    },
  ],

  // Page 11 — Heart Rate Variability (HRV)
  [METRIC_TYPE.HEART_FITNESS]: [
    {
      INDEX: 1,
      TITLE: "Peptide Therapies",
      IMPROVE_AREAS: [
        {
          INDEX: 1,
          TITLE: "Selank",
          TEXT: "Directly improves vagal tone and HRV by enhancing GABAergic signaling; greater vagal tone translates to better cardiac autonomic adaptability and resilience.",
        },
        {
          INDEX: 2,
          TITLE: "Thymosin Alpha-1",
          TEXT: "Reduces inflammatory autonomic suppression; chronic inflammation blunts HRV, so restoring immune balance allows heart rhythm to normalize.",
        },
        {
          INDEX: 3,
          TITLE: "Epitalon",
          TEXT: "Improves circadian rhythm regulation and sleep-driven HRV recovery; better melatonin cycling supports the nocturnal parasympathetic dominance essential for high HRV.",
        },
      ],
    },
    {
      INDEX: 2,
      TITLE: "Additional Therapies",
      IMPROVE_AREAS: [
        {
          INDEX: 1,
          TITLE: "NAD+ (IV or IM)",
          TEXT: "NAD+ improves myocardial mitochondrial efficiency and autonomic stability; healthier cardiac mitochondria support stronger, more adaptable heart rhythm variability.",
        },
        {
          INDEX: 2,
          TITLE: "Selank + Omega-3s",
          TEXT: "Combining Selank with omega-3 rich foods provides dual cardiovascular support: enhanced vagal tone from the peptide and cardioprotective fatty acids from nutrition.",
        },
        {
          INDEX: 3,
          TITLE: "Epitalon + Antioxidants",
          TEXT: "Epitalon paired with antioxidant-rich leafy greens supports both circadian regulation and cardiovascular health, creating optimal conditions for HRV improvement.",
        },
      ],
    },
    {
      INDEX: 3,
      TITLE: "Combination Protocols",
      IMPROVE_AREAS: [
        {
          INDEX: 1,
          TITLE: "Selank + Zone 4 HIIT",
          TEXT: "HIIT enhances stroke volume while Selank boosts vagal recovery between intervals, creating a powerful combined HRV improvement protocol.",
        },
        {
          INDEX: 2,
          TITLE: "Epitalon + Sleep Protocol",
          TEXT: "Maximizes nocturnal HRV recovery; improved circadian alignment ensures deeper non-REM sleep stages where HRV peaks.",
        },
        {
          INDEX: 3,
          TITLE: "NAD+ + Sauna Therapy",
          TEXT: "Improves both myocardial mitochondrial efficiency and heat-induced cardiovascular conditioning, synergistically elevating HRV.",
        },
      ],
    },
  ],

  // Page 12 — Metabolic Rate
  [METRIC_TYPE.RMR]: [
    {
      INDEX: 1,
      TITLE: "Peptide Therapies",
      IMPROVE_AREAS: [
        {
          INDEX: 1,
          TITLE: "CJC-1295 + Ipamorelin",
          TEXT: "Growth hormone secretagogue combination increases GH pulsatility, promoting lean mass development and thereby raising resting metabolic rate through greater metabolically active tissue.",
        },
        {
          INDEX: 2,
          TITLE: "AOD-9604",
          TEXT: "Stimulates direct lipolysis without the metabolic side effects of full GH therapy; by enhancing fat breakdown it shifts body composition toward a higher metabolic set point.",
        },
        {
          INDEX: 3,
          TITLE: "Tesamorelin",
          TEXT: "Specifically targets visceral fat metabolism, reducing metabolically disruptive fat deposits around organs, improving insulin sensitivity, and supporting a higher RMR.",
        },
      ],
    },
    {
      INDEX: 2,
      TITLE: "Additional Therapies",
      IMPROVE_AREAS: [
        {
          INDEX: 1,
          TITLE: "NAD+ (IV)",
          TEXT: "NAD+ enhances cellular energy production and metabolic throughput across all tissues; by boosting mitochondrial function it raises the baseline rate of calorie expenditure.",
        },
        {
          INDEX: 2,
          TITLE: "5-Amino-1MQ",
          TEXT: "Improves NAD-dependent metabolic pathways by inhibiting NNMT, an enzyme that consumes NAD+; this preserves cellular energy currency and supports a faster metabolism.",
        },
        {
          INDEX: 3,
          TITLE: "MOTS-c",
          TEXT: "Mitochondrial-derived peptide that promotes mitochondrial biogenesis and improves insulin sensitivity, both contributing to sustained metabolic rate elevation.",
        },
      ],
    },
    {
      INDEX: 3,
      TITLE: "Combination Protocols",
      IMPROVE_AREAS: [
        {
          INDEX: 1,
          TITLE: "CJC/Ipa + Resistance",
          TEXT: "Combining CJC-1295/Ipamorelin with strength training amplifies the muscle-building stimulus; more lean mass directly raises resting metabolic rate and daily calorie burn.",
        },
        {
          INDEX: 2,
          TITLE: "Tesamorelin + Protein",
          TEXT: "Paired with increased protein intake (1.2\u20131.6g/kg/day), supports both visceral fat reduction and muscle protein synthesis, shifting metabolic rate upward.",
        },
        {
          INDEX: 3,
          TITLE: "NAD+ + Avoid Crash Diets",
          TEXT: "NAD+ IV cycles protect mitochondrial function during caloric deficits, preventing the metabolic slowdown that comes from extreme dieting.",
        },
      ],
    },
  ],

  // Page 13 — Fat-Burning Efficiency & Mitochondrial Function
  [METRIC_TYPE.FATBURN_EFFICIENCY]: [
    {
      INDEX: 1,
      TITLE: "Peptide Therapies",
      IMPROVE_AREAS: [
        {
          INDEX: 1,
          TITLE: "MOTS-c",
          TEXT: "Promotes mitochondrial biogenesis, directly increasing the density of fat-burning organelles in cells; more mitochondria means greater capacity for fat oxidation at rest and during activity.",
        },
        {
          INDEX: 2,
          TITLE: "SS-31 (Elamipretide)",
          TEXT: "Targets the inner mitochondrial membrane, improving electron transport chain efficiency and enhancing the rate at which cells convert fat into usable energy.",
        },
        {
          INDEX: 3,
          TITLE: "Tesamorelin",
          TEXT: "Enhances substrate partitioning, directing metabolic flux toward fat utilization rather than storage, improving the body\u2019s preference for fat as a primary fuel source.",
        },
      ],
    },
    {
      INDEX: 2,
      TITLE: "Additional Therapies",
      IMPROVE_AREAS: [
        {
          INDEX: 1,
          TITLE: "NAD+ (IV)",
          TEXT: "NAD+ increases redox capacity and supports beta-oxidation, the biochemical pathway that breaks down fatty acids for energy; higher NAD+ levels mean more efficient fat burning.",
        },
        {
          INDEX: 2,
          TITLE: "MOTS-c + Omega-3s",
          TEXT: "MOTS-c paired with omega-3 rich fatty fish creates dual support for mitochondrial health and fat metabolism, both enhancing the cellular machinery for efficient fat burning.",
        },
        {
          INDEX: 3,
          TITLE: "5-Amino-1MQ",
          TEXT: "Preserves intracellular NAD+ levels by inhibiting NNMT, protecting the metabolic pathways responsible for fat oxidation and cellular energy production.",
        },
      ],
    },
    {
      INDEX: 3,
      TITLE: "Combination Protocols",
      IMPROVE_AREAS: [
        {
          INDEX: 1,
          TITLE: "MOTS-c + Zone 2 Training",
          TEXT: "The most powerful approach for mitochondrial density: Zone 2 provides the training stimulus while MOTS-c accelerates mitochondrial adaptation.",
        },
        {
          INDEX: 2,
          TITLE: "NAD+ + Cold Exposure",
          TEXT: "NAD+ IV cycles paired with cold exposure activate brown fat thermogenesis, which can increase fat-burning efficiency by 15\u201337% through the shivering response pathway.",
        },
        {
          INDEX: 3,
          TITLE: "SS-31 + Fasting Protocol",
          TEXT: "Optimizes mitochondrial membrane function during the fasted state, when the body naturally shifts to burning fat as its primary fuel.",
        },
      ],
    },
  ],
};
